import * as React from 'react';
import _debounce from 'lodash/debounce';
import classnames from 'classnames';
import { Hidden } from 'react-grid-system';
import { Loader } from '@bytebrand/fe-ui-core-autobahn/compat';
import { IconSVG } from '@bytebrand/fe-ui-core-autobahn/iconsvg';
import { Sidebar } from './partials/Sidebar';
import { Arrows } from './partials/Arrows';
import { Zoom } from './partials/Zoom';
import { ThumbsList } from './partials/ThumbsList';
import { SliderMain } from './partials/SliderMain';
import { SliderExterior } from './partials/SliderExterior';
import { SliderInterior } from './partials/SliderInterior';
import { KEYBOARD_BUTTONS } from '../../../framework/constants/cars';
import { preloadNearbyImages, updateCookieList } from '@bytebrand/fe-ui-core-autobahn/utils';
import styles from './VehicleDetailedSliderModal.styl';
export var SliderType;
(function (SliderType) {
    SliderType[SliderType["DEFAULT"] = 0] = "DEFAULT";
    SliderType[SliderType["EXTERIOR"] = 1] = "EXTERIOR";
    SliderType[SliderType["INTERIOR"] = 2] = "INTERIOR";
})(SliderType || (SliderType = {}));
const DEBOUNCE_TIME = 500;
const TOUCH_ZOOM_FACTOR = 0.005;
const WHEEL_ZOOM_FACTOR = 0.25;
export const VehicleDetailedSliderModal = (props) => {
    const { t, photos, exteriorPhotos, interiorPhoto, sliderType, showArrows, enableZoom, minZoomLevel, maxZoomLevel, zoomIncrementSize, onClose, thumbsToShow, ratioW, ratioH, handleSentryInit } = props;
    const cookieConfig = JSON.parse(localStorage.getItem('cookieConfig')) || {};
    const youtubeConfig = !cookieConfig?.marketing?.includes('youtube');
    const sidebar = React.useRef(null);
    const slider = React.useRef(null);
    const thumbs = React.useRef(null);
    const [modalWidth, setModalWidth] = React.useState(0);
    const [activeSlide, setActiveSlide] = React.useState(props.activeSlide);
    const [zoomLevel, setZoomLevel] = React.useState(props.minZoomLevel);
    const [isDimensionsSet, setIsDimensionsSet] = React.useState(false);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [isMultiTouch, setIsMultiTouch] = React.useState(false);
    const [prevTouchDistance, setPrevTouchDistance] = React.useState(0);
    const [currentSlider, setCurrentSlider] = React.useState(sliderType);
    const [showYoutube, setShowYoutube] = React.useState(youtubeConfig);
    React.useEffect(() => {
        function handleKeyboardUp(e) {
            const code = e.which || e.keyCode;
            switch (code) {
                case KEYBOARD_BUTTONS.LEFT_ARROW:
                    handlePrevUp();
                    break;
                case KEYBOARD_BUTTONS.RIGHT_ARROW:
                    handleNextUp();
                    break;
                case KEYBOARD_BUTTONS.ESC:
                    handleLightboxClose(activeSlide)();
                    break;
                default:
            }
        }
        function handleSafariPinch(e) {
            e.preventDefault();
        }
        function cancelFullscreen() {
            // @ts-ignore
            if (!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)) {
                setIsFullscreen(false);
            }
        }
        updateDimensions();
        window.addEventListener('resize', debouncedUpdateDimensions);
        window.addEventListener('keyup', handleKeyboardUp);
        document.addEventListener('gesturestart', handleSafariPinch);
        document.addEventListener('fullscreenchange', cancelFullscreen);
        document.addEventListener('mozfullscreenchange', cancelFullscreen);
        document.addEventListener('webkitfullscreenchange', cancelFullscreen);
        document.addEventListener('msfullscreenchange', cancelFullscreen);
        return () => {
            window.removeEventListener('resize', debouncedUpdateDimensions);
            window.removeEventListener('keyup', handleKeyboardUp);
            document.removeEventListener('gesturestart', handleSafariPinch);
            document.removeEventListener('fullscreenchange', cancelFullscreen);
            document.removeEventListener('mozfullscreenchange', cancelFullscreen);
            document.removeEventListener('webkitfullscreenchange', cancelFullscreen);
            document.removeEventListener('msfullscreenchange', cancelFullscreen);
        };
    }, [currentSlider]);
    React.useEffect(() => {
        updateDimensions();
    }, [isDimensionsSet]);
    React.useLayoutEffect(() => {
        updateDimensions();
    }, [modalWidth]);
    React.useEffect(() => {
        if (currentSlider === SliderType.DEFAULT) {
            const thumbsNode = thumbs.current;
            if (thumbsNode) {
                thumbsNode.slickGoTo(activeSlide, true);
            }
            setZoomLevel(minZoomLevel);
            preloadNearbyImages(photos, activeSlide, 'imageUrlLarge');
        }
    }, [activeSlide]);
    // developed by Alexandr Mardanov
    function updateDimensions() {
        const sideBarWidth = sidebar.current ? sidebar.current.clientWidth : 0;
        let maxModalWidth = 0;
        const heightBasedOnRatio = window.innerHeight * ratioW / ratioH;
        switch (currentSlider) {
            case SliderType.DEFAULT:
                maxModalWidth = !isFullscreen
                    ? ((window.innerHeight * ratioW / ratioH) * (thumbsToShow + 1) / (thumbsToShow + 2)) + sideBarWidth
                    : heightBasedOnRatio;
                break;
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                maxModalWidth = !isFullscreen
                    ? (window.innerHeight * ratioW / ratioH) + sideBarWidth
                    : heightBasedOnRatio;
                break;
            default:
        }
        const modalWidth = maxModalWidth > window.innerWidth
            ? window.innerWidth
            : maxModalWidth;
        setModalWidth(modalWidth);
        if (!isDimensionsSet) {
            setIsDimensionsSet(true);
        }
    }
    const debouncedUpdateDimensions = _debounce(() => {
        updateDimensions();
    }, DEBOUNCE_TIME);
    function handleLightboxClose(index) {
        return () => {
            onClose(index);
        };
    }
    function renderLightboxCloseIcon() {
        const iconProps = {
            className: styles.closeIcon,
            onClick: handleLightboxClose(activeSlide)
        };
        return React.createElement("div", { ...iconProps });
    }
    function handleFullscreenClose() {
        closeFullscreen(document);
        setIsFullscreen(false);
    }
    function renderFullscreenCloseIcon() {
        const iconProps = {
            className: classnames(styles.closeIcon, styles.fullscreenCloseIcon),
            onClick: handleFullscreenClose
        };
        return React.createElement("div", { ...iconProps });
    }
    function changeZoom(zoom) {
        const nextZoomLevel = Math.max(minZoomLevel, Math.min(maxZoomLevel, zoom));
        setZoomLevel(nextZoomLevel);
    }
    function handlePrevUp() {
        const sliderNode = slider.current;
        switch (currentSlider) {
            case SliderType.DEFAULT:
                if (zoomLevel !== minZoomLevel)
                    setZoomLevel(minZoomLevel);
                if (sliderNode)
                    sliderNode.slickPrev();
                break;
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                if (sliderNode)
                    sliderNode.rotateLeftEnd();
                break;
            default:
        }
    }
    function handleNextUp() {
        const sliderNode = slider.current;
        switch (currentSlider) {
            case SliderType.DEFAULT:
                if (zoomLevel !== minZoomLevel)
                    setZoomLevel(minZoomLevel);
                if (sliderNode)
                    sliderNode.slickNext();
                break;
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                if (sliderNode)
                    sliderNode.rotateRightEnd();
                break;
            default:
        }
    }
    function handlePrevDown() {
        const sliderNode = slider.current;
        switch (currentSlider) {
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                if (zoomLevel !== minZoomLevel)
                    setZoomLevel(minZoomLevel);
                if (sliderNode)
                    sliderNode.rotateLeftStart();
                break;
            default:
        }
    }
    function handleNextDown() {
        const sliderNode = slider.current;
        switch (currentSlider) {
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                if (zoomLevel !== minZoomLevel)
                    setZoomLevel(minZoomLevel);
                if (sliderNode)
                    sliderNode.rotateRightStart();
                break;
            default:
        }
    }
    function handlePrevLeave() {
        const sliderNode = slider.current;
        switch (currentSlider) {
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                if (sliderNode)
                    sliderNode.stopRotation();
                break;
            default:
        }
    }
    function handleNextLeave() {
        const sliderNode = slider.current;
        switch (currentSlider) {
            case SliderType.EXTERIOR:
            case SliderType.INTERIOR:
                if (sliderNode)
                    sliderNode.stopRotation();
                break;
            default:
        }
    }
    function handleZoomInClick() {
        changeZoom(zoomLevel + 1);
    }
    function handleZoomOutClick() {
        changeZoom(zoomLevel - 1);
    }
    function renderThumbsList() {
        const thumbsProps = {
            activeSlide,
            thumbsToShow,
            photos,
            ref: thumbs,
            onClick: (sliderIndex) => {
                const sliderNode = slider.current;
                if (sliderNode) {
                    sliderNode.slickGoTo(sliderIndex);
                }
            },
            showYoutube: showYoutube
        };
        return (React.createElement(ThumbsList, { ...thumbsProps }));
    }
    function openFullscreen(element) {
        const requestMethod = element.requestFullScreen || element.webkitRequestFullScreen || element.mozRequestFullScreen || element.msRequestFullScreen;
        requestMethod.call(element);
    }
    function closeFullscreen(element) {
        const requestMethod = element.exitFullscreen || element.mozCancelFullScreen || element.webkitExitFullscreen || element.msExitFullscreen;
        requestMethod.call(element);
    }
    function handleFullscreenClick() {
        const element = document.documentElement;
        openFullscreen(element);
        setIsFullscreen(true);
    }
    function handleTouchMove(e) {
        if (typeof e.touches === 'object' && e.touches.length === 2) {
            const touchDistance = Math.hypot(Math.abs(e.touches[0].pageX - e.touches[1].pageX), Math.abs(e.touches[0].pageY - e.touches[1].pageY));
            if (prevTouchDistance > 0) {
                changeZoom(zoomLevel + (touchDistance - prevTouchDistance) * TOUCH_ZOOM_FACTOR);
            }
            setPrevTouchDistance(touchDistance);
        }
    }
    function handleTouchStart(e) {
        if (typeof e.touches === 'object' && e.touches.length > 1) {
            setIsMultiTouch(true);
        }
    }
    function handleTouchEnd() {
        setIsMultiTouch(false);
        setPrevTouchDistance(0);
    }
    function handleWheel(e) {
        changeZoom(zoomLevel - Math.sign(e.deltaY) * WHEEL_ZOOM_FACTOR);
    }
    const updateCookies = () => {
        const cookieConfig = localStorage.getItem('cookieConfig');
        const conf = JSON.parse(cookieConfig);
        const indexToRemove = conf.marketing.indexOf('youtube');
        if (indexToRemove !== -1) {
            conf.marketing.splice(indexToRemove, 1);
        }
        const showYoutube = !conf?.marketing?.includes('youtube');
        setShowYoutube(showYoutube);
        localStorage.setItem('cookieConfig', JSON.stringify(conf));
        updateCookieList(handleSentryInit);
    };
    function renderFullscreenButton() {
        return (React.createElement("div", { className: styles.fullscreenWrapper, onClick: handleFullscreenClick },
            React.createElement(IconSVG, { name: 'fullscreenWindow', customDimensions: true, className: styles.fullscreenIcon })));
    }
    function renderSliderSwitch() {
        return (React.createElement("div", { className: styles.sliderSwitch },
            currentSlider !== SliderType.DEFAULT && photos && photos.length > 0 && (React.createElement("div", { className: classnames(styles.sliderSwitchWrapper, styles.backToFullscreen), onClick: () => setCurrentSlider(SliderType.DEFAULT) },
                React.createElement(IconSVG, { name: 'fullScreen', customDimensions: true, className: styles.sliderSwitchDefaultIcon }),
                t('slider.fullScreen'))),
            currentSlider !== SliderType.EXTERIOR && exteriorPhotos && exteriorPhotos.length > 0 && (React.createElement("div", { className: styles.sliderSwitchWrapper, onClick: () => setCurrentSlider(SliderType.EXTERIOR) },
                React.createElement(IconSVG, { name: 'rotate360', customDimensions: true, className: styles.sliderSwitch360Icon }),
                t('slider.exterior'))),
            currentSlider !== SliderType.INTERIOR && interiorPhoto && (React.createElement("div", { className: styles.sliderSwitchWrapper, onClick: () => setCurrentSlider(SliderType.INTERIOR) },
                React.createElement(IconSVG, { name: 'rotate360', customDimensions: true, className: styles.sliderSwitch360Icon }),
                t('slider.interior')))));
    }
    const scale = Math.pow(zoomIncrementSize, zoomLevel - 1);
    const sliderProps = {
        activeSlide,
        ratioW,
        ratioH,
        ref: slider
    };
    const withArrows = photos.length > 1 && showArrows;
    const arrowsProps = {
        onPrevUp: handlePrevUp,
        onNextUp: handleNextUp,
        onPrevDown: handlePrevDown,
        onNextDown: handleNextDown,
        onPrevLeave: handlePrevLeave,
        onNextLeave: handleNextLeave
    };
    const zoomProps = {
        zoomLevel,
        minZoomLevel,
        maxZoomLevel,
        handleZoomOutClick,
        handleZoomInClick
    };
    return (isDimensionsSet ? (React.createElement("div", { className: styles.vehicleDetailedSliderModal, style: { width: modalWidth }, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, onWheel: handleWheel },
        React.createElement("div", { className: styles.wrapper },
            !isFullscreen ? renderLightboxCloseIcon() : renderFullscreenCloseIcon(),
            !photos[activeSlide].videoUrl && enableZoom && currentSlider !== SliderType.INTERIOR && React.createElement(Zoom, { ...zoomProps }),
            React.createElement("div", { className: styles.top },
                React.createElement("div", { className: styles.image },
                    {
                        [SliderType.DEFAULT]: React.createElement(SliderMain, { ...sliderProps, scale, photos, onChange: setActiveSlide, updateCookies, showYoutube }),
                        [SliderType.EXTERIOR]: React.createElement(SliderExterior, { ...sliderProps, scale, isMultiTouch, photos: exteriorPhotos }),
                        [SliderType.INTERIOR]: React.createElement(SliderInterior, { ...sliderProps, zoomLevel, minZoomLevel, maxZoomLevel, photo: interiorPhoto })
                    }[currentSlider],
                    withArrows && React.createElement(Arrows, { ...arrowsProps }),
                    !isFullscreen && (React.createElement(React.Fragment, null,
                        renderSliderSwitch(),
                        React.createElement(Hidden, { xs: true, sm: true, md: true }, renderFullscreenButton())))),
                !isFullscreen && React.createElement(Sidebar, { t: t, ref: sidebar })),
            !isFullscreen && currentSlider === SliderType.DEFAULT && (React.createElement(Hidden, { xs: true, sm: true, md: true }, renderThumbsList()))))) : React.createElement(Loader, { className: styles.loader }));
};
VehicleDetailedSliderModal.defaultProps = {
    showArrows: true,
    enableZoom: true,
    activeSlide: 0,
    thumbsToShow: 7,
    ratioW: 4,
    ratioH: 3,
    minZoomLevel: 1,
    maxZoomLevel: 5,
    zoomIncrementSize: 2
};
